import { Link, useParams, useSearchParams } from 'react-router-dom';
import { usePersonSessions } from '@/hooks/useConsoleData';
import { NOT_MEASURED, when } from '@/lib/format';
import { TRACE_ENDINGS, TRACE_KINDS, TRACE_PLATFORMS, durationLabel, endingLine, tracePlatformLabel, traceEndingLabel, traceKindLabel, type SessionFilters } from '@/lib/session-data';
import s from './console.module.css';
import x from './session.module.css';

/**
 * One person's sessions, newest first, with the filters the route accepts
 * (ending, kind, platform, a date range) kept in the URL so a filtered list
 * can be sent as a link. Each row opens the two-lane page.
 */
export default function PersonSessionsPage() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const filters: SessionFilters = {
    ending: params.get('ending') ?? undefined,
    kind: params.get('kind') ?? undefined,
    platform: params.get('platform') ?? undefined,
    from: params.get('from') ?? undefined,
    to: params.get('to') ?? undefined,
    limit: 100,
  };
  const q = usePersonSessions(id ?? null, filters);
  const set = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value); else next.delete(key);
    setParams(next, { replace: true });
  };
  const rows = q.data?.traces ?? [];

  return (
    <div className={s.stack}>
      <div className={s.pageHead}>
        <div>
          <div className={s.sub}><Link to="/people">People</Link> · <Link to={`/people/${encodeURIComponent(id ?? '')}`}>person</Link> · sessions</div>
          <h1 className={s.pageTitle}>Sessions</h1>
          <div className={s.sub}>every engine session the client posted a trace for, newest first; the ones that did not finish are also on Today</div>
        </div>
      </div>

      <div className={x.toolbar} data-filters>
        <label className={s.sub}>Ending
          <select className={s.selectInline} style={{ marginLeft: 6 }} value={filters.ending ?? ''} onChange={(e) => set('ending', e.target.value)} aria-label="Ending">
            <option value="">any</option>
            {TRACE_ENDINGS.map((e) => <option key={e} value={e}>{traceEndingLabel(e)}</option>)}
          </select>
        </label>
        <label className={s.sub}>Kind
          <select className={s.selectInline} style={{ marginLeft: 6 }} value={filters.kind ?? ''} onChange={(e) => set('kind', e.target.value)} aria-label="Kind">
            <option value="">any</option>
            {TRACE_KINDS.map((k) => <option key={k} value={k}>{traceKindLabel(k)}</option>)}
          </select>
        </label>
        <label className={s.sub}>Platform
          <select className={s.selectInline} style={{ marginLeft: 6 }} value={filters.platform ?? ''} onChange={(e) => set('platform', e.target.value)} aria-label="Platform">
            <option value="">any</option>
            {TRACE_PLATFORMS.map((p) => <option key={p} value={p}>{tracePlatformLabel(p)}</option>)}
          </select>
        </label>
        <label className={s.sub}>From
          <input type="date" className={s.selectInline} style={{ marginLeft: 6 }} value={(filters.from ?? '').slice(0, 10)} onChange={(e) => set('from', e.target.value ? `${e.target.value}T00:00:00Z` : '')} aria-label="From" />
        </label>
        <label className={s.sub}>To
          <input type="date" className={s.selectInline} style={{ marginLeft: 6 }} value={(filters.to ?? '').slice(0, 10)} onChange={(e) => set('to', e.target.value ? `${e.target.value}T23:59:59Z` : '')} aria-label="To" />
        </label>
      </div>

      {q.isError && <div className={s.error}>Could not load the sessions. This page needs GET /provider/admin/people/:id/sessions.</div>}
      {q.isLoading && <div className={s.sub}>Loading the sessions.</div>}
      {q.data && rows.length === 0 && <div className={s.empty}>No session traces match. A trace exists only for sessions run on a client that posts one (web since 2026-10-11; the phones follow).</div>}

      <div className={s.list}>
        {rows.map((t) => (
          <div key={t.id} className={x.failedRow} data-session={t.peerId}>
            <div className={x.failedHead}>
              <span className={s.jTime}>{when(t.startedAt)}</span>
              <span className={t.ending === 'completed' ? '' : x.failedWhat}>{endingLine(t)}</span>
              <Link className={s.linkA} to={`/people/${encodeURIComponent(t.userId)}/sessions/${encodeURIComponent(t.peerId)}`}>Open the session</Link>
            </div>
            <div className={x.failedMeta}>
              {durationLabel(t.durationS)} · {tracePlatformLabel(t.platform)} {t.appVersion ?? ''} · {t.engineBase ?? 'engine host not recorded'} · {t.entryCount} entries{t.truncated ? ' (truncated)' : ''} · {t.result ? `result score ${t.result.score === null ? NOT_MEASURED : Math.round(t.result.score)}` : 'no result row'}{t.engineLinesFetched ? ' · engine lines fetched' : ''}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
